import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertCircle, CheckCircle2, Building2, MapPin, DollarSign, FileText, Image } from 'lucide-react';
import AdminLayout from './AdminLayout';
import {
  adminCreateProperty, adminUpdateProperty, adminGetProperties,
  adminUploadImages, adminUploadBrochure, adminUploadDeveloperLogo
} from '../../services/api';
import { CITIES, PROPERTY_TYPES } from '../../utils/constants';
import toast from 'react-hot-toast';
import './Admin.css';

const INIT = {
  title:'', propertyCategory:'PROJECT', propertyType:'Apartment', listingType:'NEW_LAUNCH',
  city:'Delhi NCR', locality:'', state:'Delhi', price:'', area:'', bedrooms:'', bathrooms:'',
  description:'', overview:'', reraNumber:'', possessionDate:'', status:'UNDER_CONSTRUCTION',
  featured:false, latitude:'', longitude:'', highlights:'', amenities:'',
  videoUrl:'', mapLink:'', vicinity:'',
};

const STEPS = [
  { n:1, label:'Basic Info', icon:Building2 },
  { n:2, label:'Location', icon:MapPin },
  { n:3, label:'Price & Specs', icon:DollarSign },
  { n:4, label:'Details', icon:FileText },
  { n:5, label:'Media', icon:Image },
];

// MUST be outside component — inside causes cursor-jump on every keystroke
const F = ({ label, name, required, hint, children, span, errors }) => (
  <div className="form-group" style={span ? {gridColumn:'1/-1'} : {}}>
    <label>{label}{required && <span className="required-star">*</span>}</label>
    {children}
    {errors[name] && <span className="field-error"><AlertCircle size={11}/> {errors[name]}</span>}
    {hint && !errors[name] && <span className="field-hint">{hint}</span>}
  </div>
);

const AdminPropertyForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id && id !== 'new');

  const [form, setForm]       = useState(INIT);
  const [images, setImages]           = useState([]);
  const [coverImage, setCoverImage]   = useState(null);
  const [floorPlans, setFloorPlans]   = useState([]);
  const [brochure, setBrochure]       = useState(null);
  const [developerLogo, setDeveloperLogo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchingEdit, setFetchingEdit] = useState(isEdit);
  const [errors, setErrors]   = useState({});
  const [step, setStep]       = useState(1);

  // Load existing property data when editing
  useEffect(() => {
    if (!isEdit) return;
    setFetchingEdit(true);
    adminGetProperties()
      .then(res => {
        const list = res.data?.data?.content || res.data?.data || res.data || [];
        const prop = list.find(p => String(p.id) === String(id));
        if (prop) {
          setForm({
            title:            prop.title || '',
            propertyCategory: prop.propertyCategory || 'PROJECT',
            propertyType:     prop.propertyType || 'Apartment',
            listingType:      prop.listingType || 'NEW_LAUNCH',
            city:             prop.city || 'Delhi NCR',
            locality:         prop.locality || '',
            state:            prop.state || 'Uttar Pradesh',
            price:            prop.price != null ? String(prop.price) : '',
            area:             prop.area != null ? String(prop.area) : '',
            bedrooms:         prop.bedrooms != null ? String(prop.bedrooms) : '',
            bathrooms:        prop.bathrooms != null ? String(prop.bathrooms) : '',
            description:      prop.description || '',
            overview:         prop.overview || '',
            reraNumber:       prop.reraNumber || '',
            possessionDate:   prop.possessionDate || '',
            status:           prop.status || 'UNDER_CONSTRUCTION',
            featured:         prop.featured || false,
            latitude:         prop.latitude != null ? String(prop.latitude) : '',
            longitude:        prop.longitude != null ? String(prop.longitude) : '',
            highlights:       Array.isArray(prop.highlights) ? prop.highlights.join('\n') : (prop.highlights || ''),
            amenities:        Array.isArray(prop.amenities)  ? prop.amenities.join(', ')  : (prop.amenities  || ''),
            videoUrl:         prop.videoUrl  || '',
            mapLink:          prop.mapLink   || '',
            vicinity:         prop.vicinity  || '',
          });
        } else {
          toast.error('Property not found');
          navigate('/admin/properties');
        }
      })
      .catch(() => { toast.error('Failed to load property'); navigate('/admin/properties'); })
      .finally(() => setFetchingEdit(false));
  }, [id, isEdit, navigate]);

  const set = (k, v) => { setForm(p => ({...p,[k]:v})); if (errors[k]) setErrors(p=>({...p,[k]:''})); };

  const validate = () => {
    const e = {};
    if (!form.title.trim() || form.title.trim().length < 5) e.title = 'Title required (min 5 chars)';
    if (!form.city)          e.city     = 'Please select a city';
    if (!form.locality.trim()) e.locality = 'Locality is required';
    if (!form.description.trim() || form.description.trim().length < 20) e.description = 'Description required (min 20 chars)';
    if (!form.price || Number(form.price) <= 0) e.price = 'A valid price is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error('Please fix the highlighted errors');
      if (errors.title)       setStep(1);
      else if (errors.city || errors.locality) setStep(2);
      else if (errors.price)  setStep(3);
      else if (errors.description) setStep(4);
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        price:     form.price     ? Number(form.price)     : null,
        area:      form.area      ? Number(form.area)      : null,
        bedrooms:  form.bedrooms  ? Number(form.bedrooms)  : null,
        bathrooms: form.bathrooms ? Number(form.bathrooms) : null,
        latitude:  form.latitude  ? Number(form.latitude)  : null,
        longitude: form.longitude ? Number(form.longitude) : null,
        possessionDate: form.possessionDate || null,
      };

      let propId;
      if (isEdit) {
        const res = await adminUpdateProperty(id, payload);
        propId = res.data?.data?.id || id;
        toast.success('Property updated!');
      } else {
        const res = await adminCreateProperty(payload);
        propId = res.data?.data?.id;
        toast.success('Property created!');
      }

      if (coverImage) {
        const fd = new FormData();
        fd.append('files', coverImage);
        fd.append('imageType', 'COVER');
        await adminUploadImages(propId, fd);
        toast.success('Feature image uploaded');
      }
      if (images.length > 0) {
        const fd = new FormData();
        images.forEach(img => fd.append('files', img));
        fd.append('imageType', 'GALLERY');
        await adminUploadImages(propId, fd);
        toast.success(`${images.length} photo(s) uploaded`);
      }
      if (floorPlans.length > 0) {
        const fd = new FormData();
        floorPlans.forEach(fp => fd.append('files', fp));
        fd.append('imageType', 'FLOOR_PLAN');
        await adminUploadImages(propId, fd);
        toast.success(`${floorPlans.length} floor plan(s) uploaded`);
      }
      if (brochure) {
        const fd = new FormData();
        fd.append('file', brochure);
        await adminUploadBrochure(propId, fd);
        toast.success('Brochure uploaded');
      }
      if (developerLogo) {
        const fd = new FormData();
        fd.append('file', developerLogo);
        await adminUploadDeveloperLogo(propId, fd);
        toast.success('Developer logo uploaded');
      }

      navigate('/admin/properties');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save. Please try again.');
    } finally { setLoading(false); }
  };

  const stepHasError = (n) => {
    if (n===1) return !!(errors.title);
    if (n===2) return !!(errors.city || errors.locality);
    if (n===3) return !!(errors.price);
    if (n===4) return !!(errors.description);
    return false;
  };

  if (fetchingEdit) return (
    <AdminLayout>
      <div className="admin-page" style={{display:'flex',alignItems:'center',justifyContent:'center',minHeight:'300px'}}>
        <p style={{color:'var(--text-3)'}}>Loading property data...</p>
      </div>
    </AdminLayout>
  );

  return (
    <AdminLayout>
      <div className="admin-page">
        <div className="admin-page__header">
          <div>
            <h1>{isEdit ? 'Edit Property' : 'Add New Property'}</h1>
            <p>Fields marked with <span style={{color:'var(--primary)'}}>*</span> are required</p>
          </div>
        </div>

        {/* Step wizard */}
        <div className="form-steps">
          {STEPS.map(s => {
            const hasErr = Object.keys(errors).length > 0 && stepHasError(s.n);
            return (
              <button key={s.n} type="button"
                className={`form-step ${step===s.n?'active':''} ${step>s.n&&!hasErr?'done':''}`}
                onClick={()=>setStep(s.n)}>
                <span className="form-step__num">
                  {hasErr ? <AlertCircle size={12} color="var(--primary)"/> : step > s.n ? <CheckCircle2 size={13}/> : s.n}
                </span>
                <span className="form-step__label">{s.label}</span>
              </button>
            );
          })}
        </div>

        <form onSubmit={handleSubmit} noValidate onKeyDown={e => { if (e.key === "Enter" && e.target.tagName !== "TEXTAREA" && step < 5) e.preventDefault(); }}>
          <div className="admin-card prop-form">

            {/* Step 1 */}
            {step === 1 && (
              <div className="prop-form__section">
                <h4><Building2 size={16}/> Basic Information</h4>
                <F errors={errors} label="Property Title" name="title" required hint="e.g. SPS Emerald Towers, Sector 150 Delhi NCR" span>
                  <input value={form.title} onChange={e=>set('title',e.target.value)} placeholder="Enter the full property name" className={errors.title?'input-error':''}/>
                </F>
                <div className="prop-form__grid">
                  <F errors={errors} label="Category" name="propertyCategory" required>
                    <select value={form.propertyCategory} onChange={e=>set('propertyCategory',e.target.value)}>
                      <option value="PROJECT">Developer Project</option>
                      <option value="RESALE">Resale / Brokerage</option>
                    </select>
                  </F>
                  <F errors={errors} label="Property Type" name="propertyType">
                    <select value={form.propertyType} onChange={e=>set('propertyType',e.target.value)}>
                      {PROPERTY_TYPES.map(t=><option key={t}>{t}</option>)}
                    </select>
                  </F>
                  <F errors={errors} label="Listing Type" name="listingType">
                    <select value={form.listingType} onChange={e=>set('listingType',e.target.value)}>
                      <option value="NEW_LAUNCH">New Launch</option>
                      <option value="SALE">Sale</option>
                      <option value="RENT">Rent</option>
                    </select>
                  </F>
                  <F errors={errors} label="Property Status" name="status">
                    <select value={form.status} onChange={e=>set('status',e.target.value)}>
                      <option value="UNDER_CONSTRUCTION">Under Construction</option>
                      <option value="READY_TO_MOVE">Ready to Move</option>
                      <option value="SOLD_OUT">Sold Out</option>
                    </select>
                  </F>
                </div>
                <label className="featured-check">
                  <input type="checkbox" checked={form.featured} onChange={e=>set('featured',e.target.checked)}/>
                  <span>⭐ Mark as <strong>Featured Property</strong> — shown on homepage</span>
                </label>
              </div>
            )}

            {/* Step 2 */}
            {step === 2 && (
              <div className="prop-form__section">
                <h4><MapPin size={16}/> Location Details</h4>
                <div className="prop-form__grid">
                  <F errors={errors} label="City" name="city" required>
                    <select value={form.city} onChange={e=>set('city',e.target.value)} className={errors.city?'input-error':''}>
                      {CITIES.map(c=><option key={c}>{c}</option>)}
                    </select>
                  </F>
                  <F errors={errors} label="Locality / Sector / Area" name="locality" required hint="e.g. Sector 150, Golf Course Road">
                    <input value={form.locality} onChange={e=>set('locality',e.target.value)} placeholder="Enter locality or sector" className={errors.locality?'input-error':''}/>
                  </F>
                  <F errors={errors} label="State" name="state">
                    <input value={form.state} onChange={e=>set('state',e.target.value)} placeholder="e.g. Uttar Pradesh"/>
                  </F>
                </div>
                <div className="prop-form__grid" style={{marginTop:'8px'}}>
                  <F errors={errors} label="Latitude" name="latitude" hint="Optional — e.g. 28.5355">
                    <input type="number" step="any" value={form.latitude} onChange={e=>set('latitude',e.target.value)} placeholder="28.5355"/>
                  </F>
                  <F errors={errors} label="Longitude" name="longitude" hint="Optional — e.g. 77.3910">
                    <input type="number" step="any" value={form.longitude} onChange={e=>set('longitude',e.target.value)} placeholder="77.3910"/>
                  </F>
                </div>
              </div>
            )}

            {/* Step 3 */}
            {step === 3 && (
              <div className="prop-form__section">
                <h4><DollarSign size={16}/> Price &amp; Specifications</h4>
                <div className="prop-form__grid-3">
                  <F errors={errors} label="Price (₹)" name="price" required hint="e.g. 12500000 for ₹1.25 Cr">
                    <input type="number" min="1" value={form.price} onChange={e=>set('price',e.target.value)} placeholder="12500000" className={errors.price?'input-error':''}/>
                  </F>
                  <F errors={errors} label="Area (sq.ft.)" name="area" hint="Built-up area">
                    <input type="number" min="1" value={form.area} onChange={e=>set('area',e.target.value)} placeholder="1850"/>
                  </F>
                  <F errors={errors} label="Bedrooms" name="bedrooms" hint="0 for studio/commercial">
                    <input type="number" min="0" max="50" value={form.bedrooms} onChange={e=>set('bedrooms',e.target.value)} placeholder="3"/>
                  </F>
                  <F errors={errors} label="Bathrooms" name="bathrooms">
                    <input type="number" min="0" max="20" value={form.bathrooms} onChange={e=>set('bathrooms',e.target.value)} placeholder="3"/>
                  </F>
                  <F errors={errors} label="RERA Number" name="reraNumber" hint="Leave blank if N/A">
                    <input value={form.reraNumber} onChange={e=>set('reraNumber',e.target.value)} placeholder="UPRERAPRJ12345"/>
                  </F>
                  <F errors={errors} label="Possession Date" name="possessionDate" hint="Expected handover date">
                    <input type="date" value={form.possessionDate} onChange={e=>set('possessionDate',e.target.value)}/>
                  </F>
                </div>
              </div>
            )}

            {/* Step 4 */}
            {step === 4 && (
              <div className="prop-form__section">
                <h4><FileText size={16}/> Description &amp; Details</h4>
                <F errors={errors} label="Full Description" name="description" required hint="Min 20 characters." span>
                  <textarea rows={5} value={form.description} onChange={e=>set('description',e.target.value)} placeholder="Describe the property in detail..." className={errors.description?'input-error':''}/>
                </F>
                <F errors={errors} label="Short Overview" name="overview" hint="1–2 sentences for listing cards." span>
                  <textarea rows={2} value={form.overview} onChange={e=>set('overview',e.target.value)} placeholder="A brief, compelling summary..."/>
                </F>
                <div className="prop-form__grid">
                  <F errors={errors} label="Key Highlights" name="highlights" hint="One highlight per line">
                    <textarea rows={4} value={form.highlights} onChange={e=>set('highlights',e.target.value)} placeholder={"RERA Registered\n80% Open Green Area\nOlympic-size Swimming Pool"}/>
                  </F>
                  <F errors={errors} label="Amenities" name="amenities" hint="Comma-separated">
                    <textarea rows={4} value={form.amenities} onChange={e=>set('amenities',e.target.value)} placeholder="Swimming Pool, Club House, Gym, 24/7 Security"/>
                  </F>
                </div>
                <div className="prop-form__grid">
                  <F errors={errors} label="Nearby Vicinity" name="vicinity" hint="Comma-separated nearby landmarks e.g. Airport 5km, School 1km" span>
                    <textarea rows={3} value={form.vicinity} onChange={e=>set('vicinity',e.target.value)} placeholder="Airport 8km, Metro Station 500m, School 1.2km, Hospital 2km, Mall 3km"/>
                  </F>
                </div>
                <div className="prop-form__grid">
                  <F errors={errors} label="Video URL (optional)" name="videoUrl" hint="YouTube or Vimeo embed URL e.g. https://youtube.com/embed/xxxxx">
                    <input value={form.videoUrl} onChange={e=>set('videoUrl',e.target.value)} placeholder="https://www.youtube.com/embed/VIDEO_ID"/>
                  </F>
                  <F errors={errors} label="Google Maps Link (optional)" name="mapLink" hint="Paste Google Maps share link — shown as interactive map">
                    <input value={form.mapLink} onChange={e=>set('mapLink',e.target.value)} placeholder="https://maps.google.com/?q=..."/>
                  </F>
                </div>
              </div>
            )}

            {/* Step 5 — Media */}
            {step === 5 && (
              <div className="prop-form__section">
                <h4><Image size={16}/> Media &amp; Files</h4>

                {/* Feature Image — prominent, full row */}
                <div className="form-group" style={{gridColumn:'1/-1',marginBottom:'8px'}}>
                  <label style={{display:'block',fontWeight:700,fontSize:'13px',marginBottom:'4px'}}>
                    ★ Feature / Hero Image <span style={{color:'var(--primary)'}}>*</span>
                  </label>
                  <p style={{fontSize:'12px',color:'#9ca3af',margin:'0 0 8px'}}>
                    This is the <strong>single large image</strong> shown at the top of the property page. Use your best photo. JPG/PNG · Min 1200×800px recommended.
                  </p>
                  <div
                    className={"file-upload-label" + (coverImage ? " has-file" : "")}
                    onClick={() => document.getElementById('cover-file-input').click()}
                    style={{cursor:'pointer',background: coverImage ? undefined : 'linear-gradient(135deg,#fdf8f8,#fff)'}}
                  >
                    {coverImage
                      ? <><CheckCircle2 size={22}/><span>{coverImage.name}</span><small>Click to change</small></>
                      : <><Image size={22}/><span>Click to upload feature image</span><small>Shown as hero on property page</small></>
                    }
                  </div>
                </div>

                <div className="prop-form__grid">
                  {/* Gallery Photos */}
                  <div className="form-group">
                    <label style={{display:'block',fontWeight:600,fontSize:'13px',marginBottom:'4px'}}>Gallery Photos</label>
                    <p style={{fontSize:'12px',color:'#9ca3af',margin:'0 0 8px'}}>Multiple photos for the gallery section · JPG/PNG/WebP</p>
                    <div
                      className={"file-upload-label" + (images.length > 0 ? " has-file" : "")}
                      onClick={() => document.getElementById('img-file-input').click()}
                      style={{cursor:'pointer'}}
                    >
                      {images.length > 0
                        ? <><CheckCircle2 size={22}/><span>{images.length} photo{images.length>1?'s':''} selected</span><small>Click to change</small></>
                        : <><Image size={22}/><span>Click to select photos</span><small>JPG, PNG, WebP</small></>
                      }
                    </div>
                  </div>

                  {/* Floor Plans */}
                  <div className="form-group">
                    <label style={{display:'block',fontWeight:600,fontSize:'13px',marginBottom:'4px'}}>Floor Plans</label>
                    <p style={{fontSize:'12px',color:'#9ca3af',margin:'0 0 8px'}}>Shown in Floor Plans tab · JPG/PNG</p>
                    <div
                      className={"file-upload-label" + (floorPlans.length > 0 ? " has-file" : "")}
                      onClick={() => document.getElementById('floorplan-file-input').click()}
                      style={{cursor:'pointer'}}
                    >
                      {floorPlans.length > 0
                        ? <><CheckCircle2 size={22}/><span>{floorPlans.length} floor plan{floorPlans.length>1?'s':''} selected</span><small>Click to change</small></>
                        : <><FileText size={22}/><span>Click to select floor plans</span><small>JPG, PNG supported</small></>
                      }
                    </div>
                  </div>

                  {/* Brochure */}
                  <div className="form-group">
                    <label style={{display:'block',fontWeight:600,fontSize:'13px',marginBottom:'4px'}}>Project Brochure (PDF)</label>
                    <p style={{fontSize:'12px',color:'#9ca3af',margin:'0 0 8px'}}>PDF only · Downloadable by visitors</p>
                    <div
                      className={"file-upload-label" + (brochure ? " has-file" : "")}
                      onClick={() => document.getElementById('pdf-file-input').click()}
                      style={{cursor:'pointer'}}
                    >
                      {brochure
                        ? <><CheckCircle2 size={22}/><span>{brochure.name}</span><small>Click to change</small></>
                        : <><FileText size={22}/><span>Click to select PDF</span><small>Project brochure</small></>
                      }
                    </div>
                  </div>

                  {/* Developer / Brand Logo */}
                  <div className="form-group">
                    <label style={{display:'block',fontWeight:600,fontSize:'13px',marginBottom:'4px'}}>Developer / Brand Logo</label>
                    <p style={{fontSize:'12px',color:'#9ca3af',margin:'0 0 8px'}}>PNG with transparent background · Shown on property page header</p>
                    <div
                      className={"file-upload-label" + (developerLogo ? " has-file" : "")}
                      onClick={() => document.getElementById('devlogo-file-input').click()}
                      style={{cursor:'pointer'}}
                    >
                      {developerLogo
                        ? <><CheckCircle2 size={22}/><span>{developerLogo.name}</span><small>Click to change</small></>
                        : <><Building2 size={22}/><span>Click to upload logo</span><small>PNG/WebP recommended</small></>
                      }
                    </div>
                  </div>
                </div>

                <p style={{fontSize:'13px',color:'#6b7280',marginTop:'16px',padding:'12px 16px',background:'#f9fafb',borderRadius:'8px',border:'1px solid #e5e7eb'}}>
                  💡 <strong>Tip:</strong> All files upload after saving. You can update them later by editing the property.
                </p>
              </div>
            )}

            {/* Footer */}
            <div className="prop-form__footer">
              <div>
                {step > 1 && (
                  <button type="button" className="admin-btn-secondary" onClick={()=>setStep(s=>s-1)}>← Back</button>
                )}
              </div>
              <div className="prop-form__actions-right">
                <button type="button" className="btn-outline-dark" onClick={()=>navigate('/admin/properties')}>Cancel</button>
                {step < 5
                  ? <button type="button" className="btn-primary" onClick={()=>setStep(s=>s+1)}>Next →</button>
                  : <button type="submit" className="btn-primary" disabled={loading}>
                      {loading ? 'Saving...' : isEdit ? '✓ Update Property' : '✓ Create Property'}
                    </button>
                }
              </div>
            </div>
          </div>
        </form>

        {/* Hidden file inputs — outside form so they never trigger submit */}
        <input type="file" id="cover-file-input" accept="image/*" style={{display:'none'}}
          onChange={e => setCoverImage(e.target.files[0] || null)} />
        <input type="file" id="img-file-input" accept="image/*" multiple style={{display:'none'}}
          onChange={e => setImages(Array.from(e.target.files))} />
        <input type="file" id="floorplan-file-input" accept="image/*" multiple style={{display:'none'}}
          onChange={e => setFloorPlans(Array.from(e.target.files))} />
        <input type="file" id="pdf-file-input" accept=".pdf" style={{display:'none'}}
          onChange={e => setBrochure(e.target.files[0] || null)} />
        <input type="file" id="devlogo-file-input" accept="image/*" style={{display:'none'}}
          onChange={e => setDeveloperLogo(e.target.files[0] || null)} />
      </div>
    </AdminLayout>
  );
};

export default AdminPropertyForm;
